import { useState, useMemo } from 'react'
import { DashboardLayout } from '../../components/layout'
import { Button, StatCard, Modal } from '../../components/ui'
import { TodoList, TodoForm, TodoFilters } from '../../features/todos'
import { useTodos } from '../../hooks/useTodos'
import { useBusinesses } from '../../hooks/useBusinesses'
import { useUsers } from '../../hooks/useUsers'
import { useToast } from '../../context/ToastContext'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

function TodosPage() {
  const {
    todos,
    counts,
    loading,
    createTodo,
    updateTodo,
    deleteTodo,
    toggleComplete,
    refetch,
    addSubtask,
    toggleSubtask,
    deleteSubtask,
    addLink,
    deleteLink,
  } = useTodos()
  const { businesses } = useBusinesses()
  const { users } = useUsers()
  const toast = useToast()

  const [showModal, setShowModal] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [formLoading, setFormLoading] = useState(false)
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    business_id: '',
  })

  // Filter todos
  const filteredTodos = useMemo(() => {
    return todos.filter(todo => {
      if (filters.status && todo.status !== filters.status) return false
      if (filters.priority && todo.priority !== filters.priority) return false
      if (filters.business_id && todo.business_id !== filters.business_id) return false
      return true
    })
  }, [todos, filters])

  const handleSubmit = async (formData) => {
    setFormLoading(true)
    const { error } = editItem
      ? await updateTodo(editItem.id, formData)
      : await createTodo(formData)
    setFormLoading(false)

    if (error) {
      toast.error(`Failed to ${editItem ? 'update' : 'add'} task`)
      return
    }

    toast.success(`Task ${editItem ? 'updated' : 'added'}`)
    setShowModal(false)
    setEditItem(null)
    refetch()
  }

  const handleDelete = async (todoId) => {
    const { error } = await deleteTodo(todoId)
    if (error) {
      toast.error('Failed to delete task')
      return
    }
    toast.success('Task deleted')
    refetch()
  }

  const handleToggle = async (todoId) => {
    const { error } = await toggleComplete(todoId)
    if (error) {
      toast.error('Failed to update task')
    }
  }

  const handleEdit = (todo) => {
    setEditItem(todo)
    setShowModal(true)
  }

  if (loading) {
    return (
      <DashboardLayout title="Todos">
        <LoadingSpinner.Overlay message="Loading tasks..." />
      </DashboardLayout>
    )
  }

  // Count overdue tasks
  const overdueTasks = todos.filter(t => {
    if (t.status === 'completed' || !t.due_date) return false
    return new Date(t.due_date) < new Date()
  }).length

  return (
    <DashboardLayout
      title="Todos"
      actions={
        <Button onClick={() => { setEditItem(null); setShowModal(true) }}>
          + Add Task
        </Button>
      }
    >
      {/* Stats */}
      <div className="grid grid-4 mb-6">
        <StatCard
          label="Pending"
          value={counts.pending}
        />
        <StatCard
          label="In Progress"
          value={counts.in_progress}
        />
        <StatCard
          label="Completed"
          value={counts.completed}
        />
        <StatCard
          label="Overdue"
          value={overdueTasks}
          changeType={overdueTasks > 0 ? 'negative' : 'neutral'}
        />
      </div>

      {/* Filters */}
      <TodoFilters
        filters={filters}
        onChange={setFilters}
        businesses={businesses}
      />

      {/* Todo List */}
      <TodoList
        todos={filteredTodos}
        users={users}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggle={handleToggle}
        onToggleSubtask={toggleSubtask}
        onAddSubtask={(todoId, title) => addSubtask(todoId, title)}
        onAdd={() => { setEditItem(null); setShowModal(true) }}
      />

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditItem(null) }}
        title={editItem ? 'Edit Task' : 'Add Task'}
      >
        <TodoForm
          initialData={editItem}
          businesses={businesses}
          users={users}
          onSubmit={handleSubmit}
          onCancel={() => { setShowModal(false); setEditItem(null) }}
          onAddSubtask={(title, assignedTo) => editItem && addSubtask(editItem.id, title, assignedTo)}
          onToggleSubtask={toggleSubtask}
          onDeleteSubtask={deleteSubtask}
          onAddLink={(url, title) => editItem && addLink(editItem.id, url, title)}
          onDeleteLink={deleteLink}
          loading={formLoading}
        />
      </Modal>
    </DashboardLayout>
  )
}

export default TodosPage
